import { supabase } from "@/integrations/supabase/client";

// Types
export type AiImportAssetType =
  | "cash"
  | "equity"
  | "fixed_income"
  | "alternative"
  | "multi_asset"
  | "unclassified"; // catch-all bucket

export interface ImportCandidate {
  id?: string; // candidate temp id (server-generated)
  asset_name: string;
  symbol?: string;
  asset_type: AiImportAssetType;
  currency: string; // ISO 4217, uppercase
  market_value: number; // numeric
  quantity?: number | null;
  notes?: string;
  source_row?: string | number;
  confidence?: number; // 0..1
  duplicate_hint?: DuplicateHint | null;
  skip?: boolean; // client-side flag to skip importing this row
}

export interface DuplicateHint {
  matched_asset_id: string;
  reason: string; // e.g., "symbol+currency match", "name fuzzy match"
  score: number; // 0..1
  fieldsCompared?: string[];
}

export type ImportJobStatus = "pending" | "processing" | "completed" | "failed" | "aborted";

export interface ImportStatus {
  jobId: string;
  status: ImportJobStatus;
  progressPct?: number; // 0..100
  message?: string;
  items?: ImportCandidate[];
  errors?: string[];
}

export interface CommitItemDecision {
  candidateId: string;
  decision: "create" | "merge" | "skip";
  edits?: Partial<ImportCandidate>;
  mergeTargetId?: string; // existing asset id for merge
}

export interface CommitPayload {
  items: CommitItemDecision[];
  // Optional: when provided, the server will also attach created/merged assets
  // to this portfolio in portfolio_allocations.
  portfolio_id?: string;
}

export interface CommitResult {
  created: number;
  merged: number;
  skipped: number;
  errors?: Array<{ candidateId: string; message: string }>;
}

// Client helpers (no secrets)
export async function startAiImport(file: File, options?: { filename?: string }) {
  const formData = new FormData();
  formData.append("file", file, options?.filename || file.name);
  formData.append("action", "start");
  const { data, error } = await supabase.functions.invoke("ai-import", { body: formData as any });
  if (error) throw error;
  return data as { jobId: string };
}

// Append an additional file to an existing job
export async function appendAiImport(jobId: string, file: File, options?: { filename?: string }) {
  const formData = new FormData();
  formData.append("file", file, options?.filename || file.name);
  formData.append("action", "append");
  formData.append("jobId", jobId);
  const { data, error } = await supabase.functions.invoke("ai-import", { body: formData as any });
  if (error) throw error;
  return data as { jobId: string; appended?: number };
}

// Convenience: start with first file then append remaining files to same job
export async function startAiImportBatch(files: File[], options?: { filenames?: string[] }) {
  const list = Array.from(files || []).filter(Boolean);
  if (list.length === 0) throw new Error('No files provided');
  const first = list[0]!;
  const start = await startAiImport(first, { filename: options?.filenames?.[0] });
  const jobId = start.jobId;
  // Append the rest sequentially
  for (let i = 1; i < list.length; i++) {
    const f = list[i]!;
    await appendAiImport(jobId, f, { filename: options?.filenames?.[i] });
  }
  return { jobId };
}

export async function getImportStatus(jobId: string): Promise<ImportStatus> {
  try {
    const { data, error } = await supabase.functions.invoke("ai-import", {
      body: { action: "status", jobId }
    });
    if (error) throw error;
    const d = data as any;
    if (d && typeof d === "object" && typeof d.status === "string") {
      return d as ImportStatus;
    }
    // Fall through to DB fallback if shape invalid
  } catch (fnErr) {
    // proceed to DB fallback
  }

  // DB fallback path: read job + items directly (RLS protected)
  const jobRes: any = await supabase
    .from("ai_import_jobs" as any)
    .select("status, progress_pct, message")
    .eq("id", jobId)
    .single();
  if (jobRes.error || !jobRes.data) {
    return { jobId, status: "failed", progressPct: 0, message: "Status unavailable" } as ImportStatus;
  }
  const itemsRes: any = await supabase
    .from("ai_import_items" as any)
    .select("id, normalized_json, candidate_json, duplicate_hint")
    .eq("job_id", jobId);
  const items = (itemsRes.data || []).map((r: any) => ({
    id: r.id,
    ...(r.normalized_json || r.candidate_json),
    duplicate_hint: r.duplicate_hint || null,
  }));
  return {
    jobId,
    status: jobRes.data.status as ImportJobStatus,
    progressPct: jobRes.data.progress_pct ?? 20,
    message: jobRes.data.message || "Processing",
    items,
  } as ImportStatus;
}

export async function abortImport(jobId: string): Promise<void> {
  const { error } = await supabase.functions.invoke("ai-import", {
    body: { action: "abort", jobId }
  });
  if (error) throw error;
}

export async function commitImport(
  jobId: string,
  payload: CommitPayload
): Promise<CommitResult> {
  const { data, error } = await supabase.functions.invoke("ai-import", {
    body: { action: "commit", jobId, ...payload }
  });
  if (error) throw error;
  return data as CommitResult;
}
